import React from "react";
import { SingleDropdownType } from "./data/singleDropdown.type";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import { RandomNumber } from "services";

/**
 * SingleDropDown component to display a dropdown menu.
 * 
 * @param {Readonly<SingleDropdownType>} props - The properties for the SingleDropDown component.
 * @returns {JSX.Element} The rendered SingleDropDown component.
 */
function SingleDropDown({
  inputLabel,
  value,
  name,
  list,
  inputHelp,
  required,
  size,
  disabled,
  onChange,
  useId = false,
  sx,
}: Readonly<SingleDropdownType>): JSX.Element {
  const requiredSuffix = required ? " *" : "";
  const labelText = inputLabel ? `${inputLabel}${requiredSuffix}` : undefined;
  const formControlRef = React.useRef<HTMLDivElement>(null);
  const [menuWidth, setMenuWidth] = React.useState<number | undefined>(undefined);
  return (
    <FormControl fullWidth variant="outlined" size={size} sx={sx} ref={formControlRef}>
      {inputLabel && (
        <InputLabel
          id={`search-select-label-${name}`}
        >{labelText}</InputLabel>
      )}
      <Select
        variant="outlined"
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            sx: {
              fontSize: '0.75rem',
              minWidth: menuWidth,
              maxWidth: menuWidth,
            },
          },
        }}
        data-testid={name}
        labelId={`search-select-label-${name}`}
        id={`search-select-${name}`}
        label={labelText}
        onChange={onChange}
        value={value}
        name={name}
        required={required}
        disabled={disabled}
        style={{ background: "white" }}
        size="small"
        onOpen={() => {
          if (formControlRef.current) {
            const width = formControlRef.current.getBoundingClientRect().width;
            setMenuWidth(width);
          }
        }}
      >
        {list?.map((option, index) => (
          <MenuItem
            data-testid={`${name}-option-${index}`}
            key={option?.text ?? option ?? RandomNumber() + 1}
            value={useId ? option?.id : option?.value ?? option}
            sx={{ fontSize: '0.75rem' }}
          >
            {option?.text ?? option}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{inputHelp}</FormHelperText>
    </FormControl>
  );
}

export default SingleDropDown;
